import { ErrorSidePanel } from '@/components/errors';
import { loadHistoryState } from '@/lib';
import { useForm } from '@deadcow-enterprises/react-forms';
import {
  ErrorCodes,
  logger,
  ModuleName,
  Screen,
  type NOSIDError,
  type SkinPageConfig,
  templates,
  useHasRequiredFormError,
  useI18n,
  widgets,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import { type AxiosError } from 'axios';
import z from 'zod';
import { mfaSetupFlowSchema } from '../../../-model/flow';
import { useActivateMfa } from './-hooks/activate-mfa.hook';
import { type ValidateProps } from './-model/props';
import { ValidateLight } from './-skins';

const KEY_PREFIX = 'Security.AuthenticatorAppCard.Setup.Validate';

export const Route = createFileRoute('/(security)/security/_mfa-setup/authenticator-app/validate/')(
  {
    component: RouteComponent,
    errorComponent: ErrorSidePanel,
    staticData: {
      module: ModuleName.Security,
      screen: Screen.CodeInput,
      setupStep: { number: 3, titleKey: `${KEY_PREFIX}.Title`, canGoBack: true },
    },
    loader: loadHistoryState(mfaSetupFlowSchema, { errorCode: ErrorCodes.AccessDenied }),
  },
);

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { exists } = useI18n();
  const { mutateAsync: activateMfa } = useActivateMfa();

  const form = useForm(
    {
      Code: {
        value: '',
        id: 'Code',
        validator: z.string().length(widgets.ValidationCodeLength, `${KEY_PREFIX}.Errors.Required`),
      },
    },
    {
      focusOnError: true,
      onError: logger.logFormErrors.bind(logger),
    },
  );

  const handleSubmit = async ({ Code }: { Code: string }) => {
    try {
      const { RecoveryCodes } = await activateMfa(Code);
      navigate({ to: '/security/authenticator-app/complete', state: { RecoveryCodes } });
    } catch (e) {
      const nosidError = (e as AxiosError).response?.data as NOSIDError | undefined;
      const key = `${KEY_PREFIX}.Errors.${nosidError?.ErrorCode ?? ''}`;
      form.addError('Code', exists(key) ? key : 'Errors.DefaultError.Message');
    }
  };

  const props: ValidateProps = {
    codeValue: form.value.Code,
    codeId: form.ids.Code ?? 'Code',
    codeErrors: form.errors.Code,
    disabled: form.loading,
    loading: form.loading,
    hasRequiredError: useHasRequiredFormError(form),
    onCodeChange: (value) => form.setValue('Code', value),
    onSubmit: form.handleSubmit(handleSubmit),
  };

  return templates.getSkinSelector(skinConfig(props));
}

function skinConfig(props: ValidateProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: <ValidateLight {...props} />,
  };
}
